import type { Random } from "../core/random.js";
import {
  ATTR_MAX,
  ATTR_MIN,
  ATTRIBUTE_KEYS,
  START_AGE,
  type Attributes,
  type AttributeKey,
  type CareerPlayer,
  type Foot,
  type Position,
} from "./model.js";
import { between } from "./rng.js";

/**
 * Tu jugador: de donde sale, como crece y como envejece.
 *
 * El crecimiento es REAL pero sutil. Los atributos van de ~50 a los 17 a
 * ~85-90 en el mejor momento, y en el campo eso se traduce como mucho en un 8 %
 * (ver `talent.ts`): lo justo para que el veterano se note mejor que el chaval,
 * no para que el juego se vuelva otro.
 */

/** Como pesa cada atributo en la media, segun donde juegas. */
export const POSITION_WEIGHTS: Record<Position, Attributes> = {
  DEL: { pace: 0.3, shooting: 0.4, passing: 0.15, stamina: 0.15 },
  MED: { pace: 0.2, shooting: 0.2, passing: 0.4, stamina: 0.2 },
  DEF: { pace: 0.3, shooting: 0.1, passing: 0.25, stamina: 0.35 },
};

/** Por donde empieza cada posicion: el delantero ya chuta, el medio ya la mueve. */
const POSITION_BIAS: Record<Position, Attributes> = {
  DEL: { pace: 3, shooting: 6, passing: -2, stamina: -3 },
  MED: { pace: 0, shooting: -3, passing: 6, stamina: 3 },
  DEF: { pace: 2, shooting: -6, passing: 0, stamina: 5 },
};

export function clampAttr(value: number): number {
  return Math.max(ATTR_MIN, Math.min(ATTR_MAX, value));
}

export function overall(player: Pick<CareerPlayer, "attributes" | "position">): number {
  const w = POSITION_WEIGHTS[player.position];
  let sum = 0;
  for (const key of ATTRIBUTE_KEYS) sum += player.attributes[key] * w[key];
  return Math.round(sum);
}

export interface NewPlayer {
  name: string;
  number: number;
  position: Position;
  foot: Foot;
}

export function createPlayer(random: Random, input: NewPlayer): CareerPlayer {
  const bias = POSITION_BIAS[input.position];
  const attributes = {} as Attributes;
  for (const key of ATTRIBUTE_KEYS) {
    attributes[key] = clampAttr(48 + bias[key] + between(random, -3, 3));
  }
  return {
    name: input.name.trim().slice(0, 16) || "Chaval",
    number: Math.max(1, Math.min(99, Math.round(input.number))),
    position: input.position,
    foot: input.foot,
    age: START_AGE,
    attributes,
    potential: 0.85 + random() * 0.3,
  };
}

/** Lo que hiciste en un partido, en lo que importa para crecer y para la nota. */
export interface MatchPerformance {
  result: "win" | "draw" | "loss";
  goalsFor: number;
  goalsAgainst: number;
  goals: number;
  assists: number;
  mvp: boolean;
  shots: number;
  passes: number;
  passesOk: number;
  recoveries: number;
}

/**
 * La nota del partido, de 3 a 10, como en los periodicos.
 *
 * Cada posicion se juzga por lo suyo: al delantero se le pide gol, al medio
 * que la mueva bien y al defensa que no le metan. Un 6 es "cumplio"; un 8 ya
 * es portada; el 10 casi nadie.
 */
export function matchGrade(position: Position, p: MatchPerformance): number {
  let grade = 6;
  grade += p.goals * (position === "DEL" ? 0.9 : 1.2);
  grade += p.assists * 0.8;
  if (p.mvp) grade += 0.8;
  grade += p.result === "win" ? 0.4 : p.result === "loss" ? -0.5 : 0;
  // Una goleada en contra salpica a todos: nadie sale bien de un 0-4.
  grade -= Math.max(0, p.goalsAgainst - p.goalsFor - 1) * 0.25;
  if (p.passes >= 5) grade += (p.passesOk / p.passes - 0.72) * (position === "MED" ? 3 : 1.5);
  grade += Math.min(p.recoveries, 6) * (position === "DEF" ? 0.15 : 0.06);
  if (position === "DEF") grade += p.goalsAgainst === 0 ? 0.6 : -0.15 * p.goalsAgainst;
  if (position === "DEL" && p.shots === 0) grade -= 0.8;
  return Math.round(Math.max(3, Math.min(10, grade)) * 10) / 10;
}

/**
 * Lo que un partido te hace crecer. Poco: una temporada de 7 partidos da un
 * par de puntos por aqui y por alla, y lo gordo lo pone la edad al acabarla.
 * Lo que haces decide DONDE creces: el que marca mejora el disparo.
 */
export function matchGrowth(player: CareerPlayer, p: MatchPerformance, grade: number): Attributes {
  const youth = player.age <= 23 ? 1.15 : player.age <= 28 ? 1 : player.age <= 31 ? 0.5 : 0.2;
  const scale = youth * player.potential * (grade >= 7 ? 1.25 : grade < 5 ? 0.6 : 1);
  const accuracy = p.passes > 0 ? p.passesOk / p.passes : 0;
  const delta: Attributes = {
    pace: 0.1,
    shooting: 0.1 + Math.min(p.goals, 3) * 0.12 + Math.min(p.shots, 6) * 0.02,
    passing: 0.1 + Math.min(p.assists, 3) * 0.12 + (p.passes >= 5 ? Math.max(0, accuracy - 0.6) * 0.5 : 0),
    stamina: 0.15 + Math.min(p.recoveries, 8) * 0.04,
  };
  for (const key of ATTRIBUTE_KEYS) delta[key] = Math.round(delta[key] * scale * 100) / 100;
  return delta;
}

export function addAttributes(base: Attributes, delta: Attributes): Attributes {
  const out = {} as Attributes;
  for (const key of ATTRIBUTE_KEYS) out[key] = clampAttr(Math.round((base[key] + delta[key]) * 100) / 100);
  return out;
}

/**
 * Lo que hace la edad al acabar la temporada. Hasta los 23 se crece mucho,
 * hasta los 28 algo, a los 30 se aguanta y a partir de ahi se baja — y la
 * velocidad es lo primero que se va, como en la vida.
 */
export function seasonAging(player: CareerPlayer, seasonGrade: number, random: Random): Attributes {
  const age = player.age;
  const base =
    age <= 20 ? 2.6 : age <= 23 ? 2 : age <= 26 ? 1.2 : age <= 28 ? 0.5 : age <= 30 ? 0 : age <= 32 ? -1.2 : -2.4;
  const form = seasonGrade >= 7 ? 1.3 : seasonGrade >= 6 ? 1 : 0.7;
  const growth = base > 0 ? base * player.potential * form : base;
  const delta = {} as Attributes;
  for (const key of ATTRIBUTE_KEYS) {
    const extraPace: number = key === "pace" && age >= 29 ? -1 : 0;
    const extraStamina: number = key === "stamina" && age >= 31 ? -0.6 : 0;
    delta[key] = Math.round((growth + extraPace + extraStamina + (random() - 0.5)) * 100) / 100;
  }
  return delta;
}

/** Atributo que mas ha subido (para que el movil te lo cuente). */
export function biggestGain(delta: Attributes): AttributeKey {
  let best: AttributeKey = "pace";
  for (const key of ATTRIBUTE_KEYS) if (delta[key] > delta[best]) best = key;
  return best;
}
